import { useEffect, useState } from 'react';
import { Square } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useVoiceCapture } from './useVoiceCapture';
import { MicVisualizer } from './MicVisualizer';

interface VoiceCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUse: (text: string) => void;
}

/**
 * Modal voice capture — desktop counterpart to mobile VoiceCaptureSheet.
 */
export function VoiceCaptureDialog({ open, onOpenChange, onUse }: VoiceCaptureDialogProps) {
  const [finalText, setFinalText] = useState('');

  const voice = useVoiceCapture({
    onResult: (text) => {
      setFinalText(text.trim());
    },
    onInterim: () => {},
  });

  useEffect(() => {
    if (open && voice.supported) {
      setFinalText('');
      voice.start();
    } else if (!open) {
      voice.stop();
      setFinalText('');
    }
    // voice.start/stop are stable enough for this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const transcript = finalText || voice.interim;
  const canUse = transcript.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Voice input</DialogTitle>
          <DialogDescription>
            Speak your request. We&apos;ll put the transcript in the composer when you&apos;re done.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <MicVisualizer analyser={voice.analyser} />
          <p
            className="min-h-[3rem] w-full text-center text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            {transcript || (voice.listening ? 'Listening…' : 'Tap the mic to start')}
          </p>
          {voice.listening ? (
            <Button variant="outline" size="sm" onClick={voice.stop}>
              <Square className="size-3 fill-current" /> Stop
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={voice.start} disabled={!voice.supported}>
              Start listening
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canUse}
            onClick={() => {
              onUse(transcript.trim());
              onOpenChange(false);
            }}
          >
            Use this
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
